// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import toggleLeaseAgreement from "@guestsupply/App/Handlers/XNG/CartLeaseAgreement";
import { xIsAuthenticated } from "@guestsupply/App/Store/Selectors/GeneralSelectors";
import { checkoutPages } from "@guestsupply/XDL/constants";
import { parseBoolean } from "@guestsupply/XDL/Utilities";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    getCurrentPromotionsDataView,
    getPromotionsDataView,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import removePromotion from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/RemovePromotion";
import translate from "@insite/client-framework/Translate";
import { PromotionModel } from "@insite/client-framework/Types/ApiModels";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, Fragment, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    /**
     * If true, displays an empty cart total UI.
     * If false, displays amounts from the cart.
     * Default value: false
     */
    isCartEmpty?: boolean;
    /**
     * If true, displays tax, shipping and handling, and total amounts.
     * If false, does not display tax, shipping and handling, or total amounts.
     * Default value: true
     */
    showTaxAndShipping?: boolean;
    cart?: Cart;
    orderPromotions?: PromotionModel[];
    shippingPromotions?: PromotionModel[];
    discountTotal?: number;
    extendedStyles?: CartTotalDisplayStyles;
    isCartPage?: boolean;
    showSeparateShippingAndHandling?: boolean;
    showMiscCharge?: boolean;
}

const mapStateToProps = (state: ApplicationState) => {
    const isOrderConfirmationPage = getCurrentPage(state).type === "OrderConfirmationPage";
    const { cartId } = state.pages.orderConfirmation;
    const promotionsData =
        isOrderConfirmationPage && cartId
            ? getPromotionsDataView(state, cartId).value
            : getCurrentPromotionsDataView(state).value;
    return {
        enableVat: getSettingsCollection(state).productSettings.enableVat,
        // XNG-CHANGES
        isAuthenticated: xIsAuthenticated(state),
        isGuest: getSession(state).isGuest,
        shipTo: getShipToState(state, getSession(state).shipToId)?.value,
        isCheckoutPage: checkoutPages.includes(getCurrentPage(state).type),
        promotionsData,
        isOrderConfirmationPage,
    };
};
const mapDispatchToProps = {
    toggleLeaseAgreement,
    // xng-change
    removePromotion,
};
type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface CartTotalDisplayStyles {
    container?: GridContainerProps;
    labelGridItem?: GridItemProps;
    valueGridItem?: GridItemProps;
    subtotalLabel?: TypographyProps;
    subtotalValue?: TypographyProps;
    promotionLabel?: TypographyProps;
    promotionValue?: TypographyProps;
    discountsLabel?: TypographyProps;
    discountsValue?: TypographyProps;
    shippingAndHandlingLabel?: TypographyProps;
    shippingAndHandlingValue?: TypographyProps;
    otherChargesLabel?: TypographyProps;
    otherChargesValue?: TypographyProps;
    taxLabel?: TypographyProps;
    taxValue?: TypographyProps;
    totalLabel?: TypographyProps;
    totalValue?: TypographyProps;
    loadingSpinner?: LoadingSpinnerProps;
    // XNG-CHANGES
    titleGridItem?: GridItemProps;
    titleText?: TypographyProps;
    dividerGridItem?: GridItemProps;
    wrapper?: InjectableCss;
    additionalShippingChargesTypography?: TypographyProps;
    totalValueGridItem?: GridItemProps;
    totalLabelGridItem?: GridItemProps;
    icon?: IconPresentationProps;
    standardFreightLabel?: TypographyProps;
    standardFreightValue?: TypographyProps;
    tooltipStyles?: TooltipPresentationProps;
    shippingAndHandlingTypography?: TypographyProps;
    youSavedAmountGridItem?: GridItemProps;
    youSavedAmount?: TypographyPresentationProps;
    linkGridItem?: GridItemProps;
    promotionRemoveLink?: LinkPresentationProps;
    promotionLabelSmall?: TypographyProps;
}

export const cartTotalDisplayStyles: CartTotalDisplayStyles = {};

const CartTotalDisplay = ({
    isCartEmpty = false,
    showTaxAndShipping = true,
    cart,
    orderPromotions,
    shippingPromotions,
    enableVat,
    extendedStyles,
    // XNG-CHANGES
    isCartPage = true,
    showSeparateShippingAndHandling = false,
    showMiscCharge = true,
    isCheckoutPage,
    removePromotion,
    promotionsData,
    isOrderConfirmationPage,
}: Props) => {
    const [styles] = React.useState(() => mergeToNew(cartTotalDisplayStyles, extendedStyles));

    // Remove un-needed OOTB code
    // if (isLoading || !cart) {
    //     return (
    //         <GridContainer {...styles.container}>
    //             {showTaxAndShipping && (
    //                 <>
    //                     <GridItem {...styles.labelGridItem}>
    //                         <Typography {...styles.totalLabel}>{translate("Total")}</Typography>
    //                     </GridItem>
    //                     <GridItem {...styles.valueGridItem}>
    //                         <LoadingSpinner {...styles.loadingSpinner} />
    //                     </GridItem>
    //                 </>
    //             )}
    //             {!showTaxAndShipping && (
    //                 <>
    //                     <GridItem {...styles.labelGridItem}>
    //                         <Typography {...styles.subtotalLabel}>
    //                             {translate("Subtotal") + (enableVat ? ` (${translate("Ex. VAT")})` : "")}
    //                         </Typography>
    //                     </GridItem>
    //                     <GridItem {...styles.valueGridItem}>
    //                         <LoadingSpinner {...styles.loadingSpinner} />
    //                     </GridItem>
    //                 </>
    //             )}
    //         </GridContainer>
    //     );
    // }

    // if (isCartEmpty) {
    //     return (
    //         <GridContainer {...styles.container}>
    //             {showTaxAndShipping && (
    //                 <>
    //                     <GridItem {...styles.labelGridItem}>
    //                         <Typography {...styles.totalLabel}>{translate("Total")}</Typography>
    //                     </GridItem>
    //                     <GridItem {...styles.valueGridItem}>
    //                         <Typography {...styles.totalValue}>{`${cart.currencySymbol} -`}</Typography>
    //                     </GridItem>
    //                 </>
    //             )}
    //             {!showTaxAndShipping && (
    //                 <>
    //                     <GridItem {...styles.labelGridItem}>
    //                         <Typography {...styles.subtotalLabel}>
    //                             {translate("Subtotal") + (enableVat ? ` (${translate("Ex. VAT")})` : "")}
    //                         </Typography>
    //                     </GridItem>
    //                     <GridItem {...styles.valueGridItem}>
    //                         <Typography {...styles.subtotalValue}>{`${cart.currencySymbol} -`}</Typography>
    //                     </GridItem>
    //                 </>
    //             )}
    //         </GridContainer>
    //     );
    // }
    // const filteredOrderPromotions = orderPromotions?.filter(o => o.amount > 0);
    // const filteredShippingPromotions = shippingPromotions?.filter(o => o.amount > 0);

    if (!cart || isCartEmpty) {
        return null;
    }

    const displayDivider =
        showTaxAndShipping ||
        !isCartPage ||
        (showSeparateShippingAndHandling &&
            (cart.showTaxAndShipping || !isCartPage) &&
            (cart as any).handlingChargesDisplay?.length > 0)
            ? ""
            : "none";

    const removePromotionHandler = (promotion: PromotionModel) => {
        removePromotion({ promotionId: promotion.id });
    };

    // xng-change: reorder the subtotal and discount
    return (
        <StyledWrapper {...styles.wrapper}>
            {/* Order Summary Section */}
            <GridContainer {...styles.container} data-test-selector={`cartTotal_${cart.id}`}>
                <GridItem {...styles.titleGridItem}>
                    <Typography {...styles.titleText}>{translate("Order Summary")}</Typography>
                </GridItem>
                <GridItem {...styles.labelGridItem}>
                    <Typography {...styles.subtotalLabel}>
                        {translate("Subtotal") + (enableVat ? ` (${translate("Ex. VAT")})` : "")}
                    </Typography>
                </GridItem>
                <GridItem {...styles.valueGridItem}>
                    <Typography {...styles.subtotalValue} data-test-selector="cartTotal_subTotal">
                        {cart.orderSubTotalDisplay}
                    </Typography>
                </GridItem>
                {/* xng-change */}
                {parseBoolean(cart?.properties?.promoIsOrderLevel) &&
                    promotionsData &&
                    promotionsData.map(promotion => {
                        return (
                            <Fragment key={promotion.id}>
                                <GridItem {...styles.labelGridItem}>
                                    <Typography
                                        {...styles.promotionLabel}
                                        data-test-selector="cartTotal_orderPromotionCode"
                                    >
                                        {promotion.name}
                                    </Typography>
                                </GridItem>
                                <GridItem {...styles.valueGridItem}>
                                    <Typography
                                        {...styles.promotionValue}
                                        data-test-selector="cartTotal_orderPromotionValue"
                                    >
                                        {`-${promotion.amountDisplay}`}
                                    </Typography>
                                </GridItem>
                                {promotion.promotionCode && (
                                    <>
                                        <GridItem {...styles.labelGridItem}>
                                            <Typography
                                                // xng-change - use different style for promotion name
                                                {...styles.promotionLabelSmall}
                                                data-test-selector="cartTotal_orderPromotionName"
                                            >
                                                {promotion.promotionCode}
                                            </Typography>
                                        </GridItem>
                                        {!isOrderConfirmationPage && (
                                            <GridItem {...styles.linkGridItem}>
                                                <Link
                                                    onClick={() => removePromotionHandler(promotion)}
                                                    data-test-selector="cartTotal_orderPromotionRemoveLink"
                                                    {...styles.promotionRemoveLink}
                                                >
                                                    {translate("Remove")}
                                                </Link>
                                            </GridItem>
                                        )}
                                    </>
                                )}
                            </Fragment>
                        );
                    })}
                {showSeparateShippingAndHandling &&
                    (cart.showTaxAndShipping || !isCartPage) &&
                    (cart as any).shippingChargesDisplay?.length > 0 && (
                        <>
                            <GridItem {...styles.labelGridItem}>
                                <Typography {...styles.subtotalLabel}>{translate("Shipping")}</Typography>
                            </GridItem>
                            <GridItem {...styles.valueGridItem}>
                                <Typography {...styles.subtotalValue} data-test-selector="cartTotal_shipping">
                                    {(cart as any).shippingChargesDisplay}
                                </Typography>
                            </GridItem>
                        </>
                    )}

                {cart.properties["showFreight"]?.toLocaleLowerCase() === "true" &&
                    !showSeparateShippingAndHandling &&
                    (cart.showTaxAndShipping || !isCartPage) &&
                    cart.shippingAndHandlingDisplay?.length > 0 &&
                    !cart.properties.totalAltWarehouseFee && (
                        <>
                            <GridItem {...styles.labelGridItem}>
                                <Typography {...styles.subtotalLabel}>
                                    {`${translate("Shipping & Handling")} ${
                                        cart.properties.isPartialShipmentRate === "true" ? " *" : ""
                                    }`}
                                </Typography>
                            </GridItem>
                            <GridItem {...styles.valueGridItem}>
                                <Typography
                                    {...styles.subtotalValue}
                                    data-test-selector="cartTotal_shippingAndHandling"
                                >
                                    {cart.shippingAndHandlingDisplay}
                                </Typography>
                            </GridItem>
                        </>
                    )}
                {/* {filteredShippingPromotions &&
                    filteredShippingPromotions.map(promotion => (
                        <Fragment key={promotion.id}>
                            <GridItem {...styles.labelGridItem}>
                                <Typography {...styles.promotionLabel}>
                                    {`${translate("Promotion")}: ${promotion.name}`}
                                </Typography>
                            </GridItem>
                            <GridItem {...styles.valueGridItem}>
                                <Typography
                                    {...styles.promotionValue}
                                    data-test-selector="cartTotal_shippingPromotionValue"
                                >
                                    {`-${promotion.amountDisplay}`}
                                </Typography>
                            </GridItem>
                        </Fragment>
                    ))} */}
                {showSeparateShippingAndHandling &&
                    (cart.showTaxAndShipping || !isCartPage) &&
                    (cart as any).handlingChargesDisplay?.length > 0 && (
                        <>
                            <GridItem {...styles.labelGridItem}>
                                <Typography {...styles.subtotalLabel}>{translate("Handling")}</Typography>
                            </GridItem>
                            <GridItem {...styles.valueGridItem}>
                                <Typography {...styles.subtotalValue} data-test-selector="cartTotal_Handling">
                                    {(cart as any).handlingChargesDisplay}
                                </Typography>
                            </GridItem>
                        </>
                    )}
                {showMiscCharge && (cart as any).otherChargesDisplay?.length > 0 && (
                    <>
                        <GridItem {...styles.labelGridItem}>
                            <Typography {...styles.subtotalLabel}>{translate("'Misc. Charge")}</Typography>
                        </GridItem>
                        <GridItem {...styles.valueGridItem}>
                            <Typography {...styles.subtotalValue} data-test-selector="cartTotal_miscChanges">
                                {(cart as any).handlingChargesDisplay}
                            </Typography>
                        </GridItem>
                    </>
                )}

                {cart.properties["totalFreightCharges"] && cart.properties["totalFreightCharges"] !== "$0.00" && (
                    <>
                        <GridItem {...styles.labelGridItem}>
                            <Typography {...styles.subtotalLabel}>
                                {siteMessage("GUEST_Total_Freight_Charges_Message", "Total Surcharges")}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.valueGridItem}>
                            <Typography {...styles.subtotalValue} data-test-selector="cartTotal_TotalSurcharges">
                                {cart.properties["totalFreightCharges"]}
                            </Typography>
                        </GridItem>
                    </>
                )}
                {(showTaxAndShipping || !isCartPage) &&
                    cart.totalTaxDisplay?.length > 0 &&
                    cart.customerOrderTaxes?.length === 0 && (
                        <>
                            <GridItem {...styles.labelGridItem}>
                                <Typography {...styles.subtotalLabel}>{translate("Est. Tax")}</Typography>
                            </GridItem>
                            <GridItem {...styles.valueGridItem}>
                                <Typography {...styles.subtotalValue} data-test-selector="cartTotal_tax">
                                    {cart.totalTaxDisplay}
                                </Typography>
                            </GridItem>
                        </>
                    )}
                {showTaxAndShipping &&
                    [...cart.customerOrderTaxes!]
                        .sort((a, b) => a.sortOrder - b.sortOrder)
                        .map(tax => {
                            const key = `${tax.taxCode}_${tax.sortOrder}`;
                            return (
                                <Fragment key={key}>
                                    <GridItem {...styles.labelGridItem}>
                                        <Typography {...styles.subtotalLabel}>
                                            {tax.taxDescription || translate("Tax")}
                                        </Typography>
                                    </GridItem>
                                    <GridItem {...styles.valueGridItem}>
                                        <Typography {...styles.subtotalValue}>{tax.taxAmountDisplay}</Typography>
                                    </GridItem>
                                </Fragment>
                            );
                        })}

                {cart.properties.totalAltWarehouseFee && cart.shippingAndHandlingDisplay?.length > 0 && (
                    <>
                        <GridItem {...styles.labelGridItem}>
                            <Typography {...styles.shippingAndHandlingLabel}>
                                <Typography {...styles.shippingAndHandlingTypography}>
                                    {translate("Total Shipping & Handling")}
                                </Typography>
                                <Tooltip
                                    {...styles.tooltipStyles}
                                    text={siteMessage("Custom_Total_Shipping_And_Handling_Message") as string}
                                />
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.valueGridItem}>
                            <Typography
                                {...styles.shippingAndHandlingValue}
                                data-test-selector="cartTotal_totalShippingAndHandlingAmount"
                            >
                                {cart.currencySymbol +
                                    (cart.shippingAndHandling + +cart.properties.totalAltWarehouseFee).toFixed(2)}
                            </Typography>
                        </GridItem>
                    </>
                )}

                {!showSeparateShippingAndHandling &&
                    (showTaxAndShipping || !isCartPage) &&
                    cart.shippingAndHandlingDisplay?.length > 0 &&
                    cart.properties.totalAltWarehouseFee && (
                        <>
                            <GridItem {...styles.labelGridItem}>
                                <Typography {...styles.standardFreightLabel}>
                                    {`${translate("Standard Freight")} ${
                                        cart.properties.isPartialShipmentRate === "true" ? " *" : ""
                                    }`}
                                </Typography>
                            </GridItem>
                            <GridItem {...styles.valueGridItem}>
                                <Typography
                                    {...styles.standardFreightValue}
                                    data-test-selector="cartTotal_standardFreight"
                                >
                                    {cart.shippingAndHandlingDisplay}
                                </Typography>
                            </GridItem>
                        </>
                    )}
                {cart.properties.totalAltWarehouseFee && (
                    <>
                        <GridItem {...styles.labelGridItem}>
                            <Typography {...styles.standardFreightLabel}>{translate("Alternate DC Fee")}</Typography>
                        </GridItem>
                        <GridItem {...styles.valueGridItem}>
                            <Typography {...styles.standardFreightValue} data-test-selector="cartTotal_alternateDC">
                                {cart.properties.totalAltWarehouseFeeDisplay}
                            </Typography>
                        </GridItem>
                    </>
                )}

                <GridItem {...styles.dividerGridItem} {...{ displayDivider }} />

                {(showTaxAndShipping || !isCartPage) && (
                    <>
                        <GridItem {...styles.totalLabelGridItem}>
                            <Typography {...styles.totalLabel}>{translate("Total")}</Typography>
                        </GridItem>
                        <GridItem {...styles.totalValueGridItem}>
                            <Typography {...styles.totalValue} data-test-selector="cartTotal_orderGrandTotalDisplay">
                                {cart.orderGrandTotalDisplay}
                            </Typography>
                        </GridItem>
                    </>
                )}
                {showSeparateShippingAndHandling &&
                    (cart.showTaxAndShipping || !isCartPage) &&
                    (cart as any).handlingChargesDisplay?.length > 0 && (
                        <>
                            <GridItem {...styles.labelGridItem}>
                                <Typography {...styles.taxLabel}>{translate("Handling")}</Typography>
                            </GridItem>
                            <GridItem {...styles.valueGridItem}>
                                <Typography {...styles.taxValue} data-test-selector="cartTotal_handling">
                                    {(cart as any).handlingChargesDisplay}
                                </Typography>
                            </GridItem>
                        </>
                    )}
                {!!Number(cart?.properties?.promoTotalAmount) && (
                    <GridItem {...styles.youSavedAmountGridItem}>
                        <Typography {...styles.youSavedAmount}>{translate("You Saved")}</Typography>
                        <Typography {...styles.youSavedAmount}>{cart?.properties?.promoTotalAmountDisplay}</Typography>
                    </GridItem>
                )}
                {(cart.properties.isPartialShipmentRate?.toLowerCase() === "true" ||
                    cart.properties["showFreight"]?.toLowerCase() === "false") && (
                    <GridItem width={12}>
                        <Typography {...styles.additionalShippingChargesTypography}>
                            {siteMessage(
                                "GUEST_Cart_OrderSummary_AdditionalShippingCharges",
                                "Additional shipping charges will apply",
                            )}
                        </Typography>
                    </GridItem>
                )}
            </GridContainer>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(CartTotalDisplay);
